import { apiFetch, jsonInit } from './api';
import { openPaddleCheckout, type PaddleCheckoutSession } from './paddle';

export interface BillingSubscription {
  id: string;
  status: 'active' | 'canceled' | 'past_due' | 'paused' | 'trialing';
  next_billed_at?: string;
  current_period_ends_at?: string;
  items: {
    price_id: string;
    product_name?: string;
    quantity: number;
    unit_amount?: string;
    currency?: string;
    interval?: string;
  }[];
  scheduled_change?: { action: string; effective_at: string };
}

export interface BillingState {
  provider: string;
  configured: boolean;
  linked: boolean;
  plan?: string;
  subscription?: BillingSubscription;
}

export function hasPremiumAccess(state: BillingState | null | undefined): boolean {
  const paidPlan = state?.plan === 'pro' || state?.plan === 'enterprise';
  const usableStatus = !state?.subscription || state.subscription.status === 'active' || state.subscription.status === 'trialing';
  return Boolean(paidPlan && usableStatus);
}

export async function getBillingState(): Promise<BillingState> {
  const response = await apiFetch('/api/v1/billing/subscription');
  const data = await response.json<BillingState & { error?: { message?: string } }>();
  if (!response.ok) throw new Error(data.error?.message ?? 'Unable to load subscription status.');
  return data;
}

export async function launchProCheckout(): Promise<BillingState | null> {
  const response = await apiFetch('/api/v1/billing/checkout', jsonInit('POST', { plan: 'pro' }));
  const data = await response.json<PaddleCheckoutSession & { error?: { message?: string } }>();
  if (!response.ok) throw new Error(data.error?.message ?? 'Unable to create a secure Paddle checkout.');

  const result = await openPaddleCheckout(data);
  if (result === 'closed') return null;

  // Paddle.js confirms payment first. Poll the backend, which can establish
  // the link from either a signed webhook or the server-owned transaction
  // record, and unlock only after current provider state is active.
  for (let attempt = 0; attempt < 12; attempt += 1) {
    if (attempt > 0) await new Promise(resolve => window.setTimeout(resolve, 1000));
    const billing = await getBillingState();
    if (hasPremiumAccess(billing)) return billing;
  }
  throw new Error('Payment completed, but subscription activation is still processing. Refresh in a moment.');
}
